import {
	AddProjectTimeEvent,
	AddWorkingTimeEvent, AddWorkingTimeNoticeEvent,
	CombinedTimelineDayComponentConfig,
	WorkingTimeTimelineDayContent,
	WorkingTimeTypeFilter
} from "../timelineTypes";
import {TimelineContextMenu} from "../timelineContextMenu";
import {CombinedTimelineDayItems} from "./combinedTimelineDayItems";
import {CombinedTimelineDayRequests} from "./combinedTimelineDayRequests";
import {CombinedTimelineDayBasedWorkingTimes} from "./combinedTimelineDayBasedWorkingTimes";
import {CombinedTimelineDayNotices} from "./combinedTimelineDayNotices";
import {CombinedTimelineDayTitle} from "./combinedTimelineDayTitle";
import {CombinedTimelineDayHoliday} from "./combinedTimelineDayHoliday";
import {EVENT_PROJECT_TIME_ADD, EVENT_WORKING_TIME_ADD, EVENT_WORKING_TIME_NOTICE_ADD} from "../timelineConstants";
import {
	getDialogPresetDateTime,
	hasWorkingTimeTypesForNotices,
	hasWorkingTimeTypesForVacation,
	hasWorkingTimeTypesForWorkingTime
} from "../timelineHelper";
import {CombinedTimelineDayValidations} from "./combinedTimelineDayValidations";
import {
	getWorkingTimeTypesForAddAbsence,
	getWorkingTimeTypesForAddWorkingTime,
	isProjectTimeDateTimeChangeAllowed
} from "@/shared/securityHelper";
import {isProjectTimeIncluded} from "@/features/timeline/recordingProcedure";
import {Temporal} from "@/shared/utils/datefunctions";
import {CombinedRecordingStatus, WorkingTime} from "@/shared/timr";

/**
 * Component to draw a single (combined) timeline day with all its content like title, validations, holiday, requests, day based working times and items.
 * <code>TimelineDaysView} or {@link TimelineWeekView</code> create and append this component to draw each day in the timeline.
 *
 * Most of the content of this component is built using sub-components for each content type, which is appended to the day container.*
 * - <code>CombinedTimelineDayTitle</code> to render the title for a specific day. If available this component is appended to the day container.
 * - <code>CombinedTimelineDayValidations</code> to render the validations for a specific day. If available this component is appended to the day container.
 * - <code>CombinedTimelineDayHoliday</code> to render the holiday for a specific day. If available this component is appended to the day container.
 * - <code>CombinedTimelineDayRequests</code> to render the working time requests for a specific day. If available this component is appended to the day container.
 * - <code>CombinedTimelineDayBasedWorkingTimes</code> to render the day based working times for a specific day. If available this component is appended to the day container.
 * - <code>CombinedTimelineDayItems</code> to render the working time items for a specific day. If available this component is appended to the day container (including project time items if the feature is available).
 *
 * This component is used for the "combined timeline" meaning working time + project time feature is available and the "working time only timeline".
 * If there are project times and the feature is available, these are rendered inside the <code>CombinedTimelineDayItems</code> component as subitems of working time items.
 * If the project time feature is not available this will be empty and it behaves as a working time only timeline day.
 *
 * For an overview of the timeline architecture, have a look into the UML diagram "timeline_web_components.png"
 */
export class CombinedTimelineDay extends HTMLElement {
	private _timelineDay: WorkingTimeTimelineDayContent;
	private _config: CombinedTimelineDayComponentConfig;
	private _combinedRecordingStatus: CombinedRecordingStatus;
	private _timelineDayContainer: HTMLElement;
	private _timelineItemsComponent: CombinedTimelineDayItems;

	set timelineDay(day: WorkingTimeTimelineDayContent) {
		this._timelineDay = day;
	}

	get timelineDay(): WorkingTimeTimelineDayContent {
		return this._timelineDay;
	}

	get config(): CombinedTimelineDayComponentConfig {
		return this._config;
	}

	set config(value: CombinedTimelineDayComponentConfig) {
		this._config = value;
	}

	set combinedRecordingStatus(value: CombinedRecordingStatus) {
		this._combinedRecordingStatus = value;

		if (this._timelineItemsComponent) {
			this._timelineItemsComponent.combinedRecordingStatus = value;
		}
	}

	get combinedRecordingStatus(): CombinedRecordingStatus {
		return this._combinedRecordingStatus;
	}

	/**
	 * Main entry point to render the timeline day with all its content.
	 *
	 * Base template for the component (title is always present, other content like validations, holiday, requests, day based working times and items are appended to the day container, if available)
	 * The generated html structure would look something like this:
	 * <combined-timeline-day>
	 *   <combined-timeline-day-title>...</combined-timeline-day-title>
	 *   <div class="timeline-day">
	 *     <combined-timeline-day-validations>...</combined-timeline-day-validations>
	 *     <combined-timeline-day-holiday>...</combined-timeline-day-holiday>
	 *     <combined-timeline-day-notices>...</combined-timeline-day-notices>
	 *     <combined-timeline-day-requests>...</combined-timeline-day-requests>
	 *     <combined-timeline-day-based-working-times>...</combined-timeline-day-based-working-times>
	 *     <combined-timeline-day-items>...</combined-timeline-day-items>
	 *   </div>
	 * </combined-timeline-day>
	 */
	public connectedCallback(): void {
		// create day container
		const div = document.createElement("div");
		div.className = "timeline-day";
		this.appendChild(div);

		this.drawTitle();
		const dayContainer = this.getTimelineDayContainer();
		if (!dayContainer) {
			return;
		}

		this.drawNotices(dayContainer);
		this.drawValidations(dayContainer);
		this.drawHolidays(dayContainer);
		this.drawRequests(dayContainer);
		this.drawDayBasedWorkingTimes(dayContainer);
		this.drawItems(dayContainer);
		this.drawEmptyInfo(dayContainer);
	}

	private drawTitle(): void {
		const titleComponent = document.createElement("combined-timeline-day-title") as CombinedTimelineDayTitle;
		if (!titleComponent) {
			return;
		}
		titleComponent.timelineDay = this.timelineDay;
		titleComponent.config = this.config;
		titleComponent.contextMenuForDate = (date) => this.getContextMenuForDate(date);
		this.getTimelineDayContainer().before(titleComponent);
	}

	private drawEmptyInfo(dayContainer: HTMLElement): void {
		if (this.timelineDay.items.length > 0 ||
			this.timelineDay.workingTimeRequests.length > 0 ||
			this.timelineDay.workingTimeNotices.length > 0 ||
			this.timelineDay.workingTimeNoticeRequests.length > 0 ||
			this.timelineDay.dayBasedWorkingTimes.length > 0 ||
			this.timelineDay.validationResults.length > 0 ||
			this.timelineDay.holiday !== null) {
			return;
		}
		const emptyInfoEl = document.createElement("div");
		emptyInfoEl.dataset.emptyInfo = "";
		emptyInfoEl.classList.add("timeline-empty-info");
		emptyInfoEl.innerText = messages["timeline.info.empty"];
		dayContainer.appendChild(emptyInfoEl);
	}

	private drawValidations(dayContainer: HTMLElement): void {
		if (this.timelineDay.validationResults.length === 0) {
			return;
		}
		const validationsComponent = document.createElement("combined-timeline-day-validations") as CombinedTimelineDayValidations;
		validationsComponent.timelineDay = this.timelineDay;
		validationsComponent.config = this.config;
		validationsComponent.contextMenuForDate = (date) => this.getContextMenuForDate(date);

		dayContainer.appendChild(validationsComponent);
	}

	private drawHolidays(dayContainer: HTMLElement): void {
		if (this.timelineDay.holiday === null) {
			return;
		}
		const holidayComponent = document.createElement("combined-timeline-day-holiday") as CombinedTimelineDayHoliday;
		holidayComponent.timelineDay = this.timelineDay;

		dayContainer.appendChild(holidayComponent);
	}

	private drawDayBasedWorkingTimes(dayContainer: HTMLElement): void {
		if (this.timelineDay.dayBasedWorkingTimes.length === 0) {
			return;
		}
		const dayBasedWorkingTimesComponent = document.createElement("combined-timeline-day-based-working-times") as CombinedTimelineDayBasedWorkingTimes;
		dayBasedWorkingTimesComponent.timelineDay = this.timelineDay;
		dayBasedWorkingTimesComponent.config = this.config;

		dayContainer.appendChild(dayBasedWorkingTimesComponent);
	}

	private drawRequests(dayContainer: HTMLElement): void {
		if (this.timelineDay.workingTimeRequests.length === 0) {
			return;
		}
		const requestsComponent = document.createElement("combined-timeline-day-requests") as CombinedTimelineDayRequests;
		requestsComponent.timelineDay = this.timelineDay;

		dayContainer.appendChild(requestsComponent);
	}

	private drawNotices(dayContainer: HTMLElement): void {
		if (this.timelineDay.workingTimeNotices.length === 0 && this.timelineDay.workingTimeNoticeRequests.length === 0) {
			return;
		}
		const noticesComponent = document.createElement("combined-timeline-day-notices") as CombinedTimelineDayNotices;
		noticesComponent.timelineDay = this.timelineDay;

		dayContainer.appendChild(noticesComponent);
	}

	private drawItems(dayContainer: HTMLElement): void {
		if (this.timelineDay.items.length === 0) {
			return;
		}
		const itemsComponent = document.createElement("combined-timeline-day-items") as CombinedTimelineDayItems;
		itemsComponent.combinedRecordingStatus = this.combinedRecordingStatus;
		itemsComponent.timelineDay = this.timelineDay;
		itemsComponent.config = this.config;

		this._timelineItemsComponent = itemsComponent;
		dayContainer.appendChild(itemsComponent);
	}

	private getTimelineDayContainer(): HTMLElement {
		if (!this._timelineDayContainer) {
			this._timelineDayContainer = this.querySelector(".timeline-day");
		}
		return this._timelineDayContainer;
	}

	private getContextMenuForDate(date: Temporal.PlainDate | null): TimelineContextMenu<WorkingTime> {
		return new TimelineContextMenu()
			.addMenuEntry({
				label: "timeline.workingTime.add",
				action: () => this.addWorkingTimeForDate(false, date),
				active: () => hasWorkingTimeTypesForWorkingTime(this.config)
			})
			.addMenuEntry({
				label: "timeline.workingTime.add.absence",
				action: () => this.addWorkingTimeForDate(true, date),
				active: () => hasWorkingTimeTypesForVacation(this.config)
			})
			.addMenuEntry({
				label: "timeline.workingTime.notice.add",
				show: () => hasWorkingTimeTypesForNotices(this.config),
				action: () => this.addWorkingTimeNoticeForDate(date)
			})
			.addMenuEntry({
				label: "timeline.workingTime.addProjectTime",
				show: () => isProjectTimeIncluded(this.config.activeRecordingProcedure),
				action: () => {
					const defaultDateTime = getDialogPresetDateTime(date);
					this.onAddProjectTimeEventClicked(defaultDateTime.start, defaultDateTime.end);
				},
				active: () => isProjectTimeDateTimeChangeAllowed(this.config.reportingOtherUsers)
			});
	}

	private addWorkingTimeForDate(absence: boolean, date: Temporal.PlainDate | null): void {
		const {start, end} = this.getAddWorkingTimeRange(date);

		this.onAddWorkingTimeEventClicked(
			absence ? getWorkingTimeTypesForAddAbsence : getWorkingTimeTypesForAddWorkingTime,
			start,
			end
		);
	}

	private getAddWorkingTimeRange(date: Temporal.PlainDate | null): { start: Temporal.ZonedDateTime; end: Temporal.ZonedDateTime } {
		return getDialogPresetDateTime(date);
	}

	private addWorkingTimeNoticeForDate(date: Temporal.PlainDate | null): void {
		this.dispatchEvent(new CustomEvent<AddWorkingTimeNoticeEvent>(EVENT_WORKING_TIME_NOTICE_ADD, {
			detail: {start: date, end: date},
			bubbles: true
		}));
	}

	private onAddWorkingTimeEventClicked(workingTimeTypeFilter: WorkingTimeTypeFilter, start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null): void {
		const customEvent = new CustomEvent<AddWorkingTimeEvent>(
			EVENT_WORKING_TIME_ADD, {
				detail: {
					workingTimeTypeFilter: workingTimeTypeFilter,
					start: start,
					end: end
				},
				bubbles: true
			}
		);

		this.dispatchEvent(customEvent);
	}

	private onAddProjectTimeEventClicked(start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null): void {
		const customEvent = new CustomEvent<AddProjectTimeEvent>(
			EVENT_PROJECT_TIME_ADD, {
				detail: {
					start: start,
					end: end
				},
				bubbles: true
			});
		this.dispatchEvent(customEvent);
	}
}
